import { isServer } from 'utils/helpers/browser';

const getEnvValue = <T extends string | number | boolean>(
  env: string | boolean | undefined,
  defaultValue: T,
): T => {
  try {
    if (env === undefined) return defaultValue;

    if (typeof defaultValue === typeof env) return env as T;

    if (!Number.isNaN(Number(env))) return Number(env) as T;

    return env as T;
  } catch {
    return defaultValue;
  }
};

const getBooleanEnv = (env?: string) => {
  const truthyValues = ['true', '1', 'yes', 'ok'];

  if (env) return truthyValues.includes(env.toLowerCase());

  return false;
};

export interface TClientConfig {
  appSignalKey: string;
  giphyApiKey: string;
  isProduction: boolean;
  zmyleWidgetUrl: string;
  enableRecaptcha: boolean;
  recaptchaSiteKey: string;
  nonce?: string | null;
  googleTagManagerId: string;
  facebookPixelId: string;
  linkedinPartnerId: string;
  matomo: {
    url: string;
    siteId: string;
  };
}

export class ClientConfig {
  // eslint-disable-next-line no-use-before-define
  private static instance: ClientConfig;

  private config: TClientConfig;

  private constructor() {
    const isProduction = process.env.NODE_ENV === 'production';

    this.config = {
      appSignalKey: getEnvValue(process.env.NEXT_PUBLIC_APP_SIGNAL_KEY, ''),
      giphyApiKey: getEnvValue(process.env.NEXT_PUBLIC_GIPHY_API_KEY, ''),
      enableRecaptcha: getBooleanEnv(process.env.NEXT_PUBLIC_ENABLE_RECAPTCHA),
      recaptchaSiteKey: getEnvValue(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
        '',
      ),
      googleTagManagerId: getEnvValue(
        process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID,
        '',
      ),
      facebookPixelId: getEnvValue(
        process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
        '',
      ),
      linkedinPartnerId: getEnvValue(
        process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID,
        '',
      ),
      matomo: {
        url: getEnvValue(process.env.NEXT_PUBLIC_MATOMO_URL, ''),
        siteId: getEnvValue(process.env.NEXT_PUBLIC_MATOMO_SITE_ID, ''),
      },
      nonce: null, // to be set later by the server (if needed
      isProduction,
      zmyleWidgetUrl: isProduction
        ? 'https://edge.zmyle.de/static/zmyle-cookie-widget/zmyle-cookie-widget.min.js'
        : 'https://edge.zmyle.de/static/zmyle-cookie-widget/zmyle-cookie-widget-dev.min.js',
    };

    this.setAnalyticsConfig();
  }

  public static getInstance() {
    if (!ClientConfig.instance) ClientConfig.instance = new ClientConfig();

    return ClientConfig.instance;
  }

  private setAnalyticsConfig() {
    if (isServer()) return;

    window.analytics = {};

    if (this.config.facebookPixelId) {
      window.analytics.facebookPixelId = this.config.facebookPixelId;
    }

    if (this.config.linkedinPartnerId) {
      window.analytics.linkedinPartnerId = this.config.linkedinPartnerId;
    }
  }

  setConfig(config: Partial<TClientConfig>) {
    this.config = { ...this.config, ...config };

    this.setAnalyticsConfig();
  }

  public getConfig() {
    return this.config;
  }
}
